<script setup lang="ts">
import RelatedGames from '../../tournament/RelatedGames.vue';
import MissionReward from '../MissionReward.vue';
import { useMissionCard } from '~/composables/useMissionCard';

const props = defineProps<{
  mission: TMissionOrBadge;
  isFeatured?: boolean;
}>();

const emit = defineEmits<{
  missionOptedIn: [missionId: number];
}>();

const {
  mission,
  isFeatured,
  maxRelatedGames,
  missionProgress,
  missionProgressText,
  isModalVisible,
  isRelatedGamesModalVisible,
  isRewardModalVisible,
  formattedHintText,
  rewardData,
  buttonIcons,
  buttonState,
  handleButtonClick,
  handleMissionClick,
} = useMissionCard(props, (missionId) => emit('missionOptedIn', missionId));
</script>

<template>
  <div :class="$style.cardWrapper">
    <div
      :class="[$style.missionCard, isFeatured ? $style.featuredMission : '']"
      draggable="false"
      @dragstart.prevent
    >
      <!-- Ribbon no topo centralizado -->
      <div
        v-if="mission.ribbon"
        :class="$style.ribbon"
      >
        <span :class="$style.ribbonText">{{ mission.ribbon }}</span>
      </div>

      <!-- Header: Image + Info -->
      <div :class="$style.headerSection">
        <SmarticoApostatudoSmarticoImage
          :alt="mission.name"
          :class="$style.missionImage"
          :src="mission.image"
          @click="handleMissionClick('reward')"
        />
        <div :class="$style.missionInfo">
          <p
            :class="$style.missionName"
            @click="handleMissionClick('reward')"
          >
            {{ mission.name }}
          </p>
          <p
            :class="$style.missionDescription"
            @click="handleMissionClick('reward')"
          >
            {{ mission.description }}
          </p>
        </div>
      </div>

      <!-- Reward Section -->
      <div
        v-if="mission.reward"
        :class="$style.rewardSection"
        @click="handleMissionClick('reward')"
      >
        <NuxtIcon
          :class="$style.rewardIcon"
          :filled="true"
          name="mission/gift"
        />
        <span :class="$style.rewardText">{{ rewardData.reward }}</span>
      </div>

      <!-- Progress Section -->
      <div :class="$style.progressSection">
        <div :class="$style.progressBarContainer">
          <div :class="$style.progressBar">
            <div
              :class="$style.progressFill"
              :style="{ width: `${missionProgress}%` }"
            ></div>
            <span :class="$style.progressText">{{ missionProgressText }}</span>
          </div>
          <NuxtIcon
            :class="$style.progressIconLeft"
            :filled="true"
            name="mission/energy"
          />
          <NuxtIcon
            :class="$style.progressIconRight"
            :filled="true"
            name="mission/trophy"
          />
        </div>
      </div>

      <!-- Footer: Games + Button -->
      <div :class="$style.footerSection">
        <!-- Related Games -->
        <div
          :class="$style.gamesSection"
          @click="handleMissionClick('relatedGames')"
        >
          <div :class="$style.gamesStack">
            <div
              v-for="(related, index) in mission.related_games?.slice(
                0,
                maxRelatedGames,
              )"
              :key="index"
              :class="$style.gameThumb"
            >
              <NuxtImg
                :class="$style.gameImage"
                :src="related?.game_public_meta?.image"
              />
            </div>
          </div>
          <span :class="$style.gamesLabel">Jogos</span>
        </div>

        <!-- Action Button -->
        <button
          v-if="buttonState"
          :class="$style.actionButton"
          :disabled="buttonState.disabled"
          @click.stop="handleButtonClick"
        >
          <NuxtIcon
            v-for="(icon, index) in buttonIcons"
            :key="index"
            :class="$style.buttonIcon"
            :name="icon"
          />
          <span :class="$style.buttonText">{{ buttonState.text }}</span>
        </button>
      </div>

      <!-- Modals -->
      <BaseModalMissionModal v-model="isModalVisible">
        <template #header>
          <NuxtIcon
            class="text-xl text-white"
            name="mission/info"
          />
          <p class="text-lg font-bold text-white">Termos e condições</p>
        </template>
        <div v-if="mission.hint_text">
          <p
            v-for="(line, index) in formattedHintText"
            :key="index"
            class="mb-2 text-white/50 font-normal text-[13px]"
          >
            {{ line }}
          </p>
        </div>
      </BaseModalMissionModal>

      <RelatedGames
        v-model:modal-visible="isRelatedGamesModalVisible"
        :is-loading="false"
        :items="mission.related_games"
        style="display: none"
        title="Jogos Elegíveis"
      />

      <MissionReward
        v-model:modal-visible="isRewardModalVisible"
        :mission="mission"
      />
    </div>
  </div>
</template>

<style module lang="postcss">
/* === CARD WRAPPER === */
.cardWrapper {
  width: 100%;
  min-width: 0;
  padding-top: 10px;
  /* Isola contexto de composição para evitar vazamento de blur/shadow */
  isolation: isolate;
  contain: layout style paint;
}

/* Dentro de swipers: largura fixa */
:global(.afiliado-swiper__items) .cardWrapper {
  width: 165px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .cardWrapper {
    padding-top: 14px;
  }

  :global(.afiliado-swiper__items) .cardWrapper {
    width: 260px;
  }
}

.missionCard {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 9px;
  border-radius: 6px;
  backdrop-filter: blur(6px);
  background: linear-gradient(
    180deg,
    rgba(27, 7, 1, 0.4) 0%,
    rgba(129, 33, 4, 0.4) 100%
  );
  overflow: visible;
  user-select: none;
  gap: 5px;
  min-width: 0;
}

@media (min-width: 640px) {
  .missionCard {
    padding: 14px;
    border-radius: 9px;
    gap: 8px;
  }
}

.missionCard::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  padding: 0.58px;
  background: linear-gradient(180deg, #1d2329 0%, #f66a00 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: 0;
}

@media (min-width: 640px) {
  .missionCard::before {
    padding: 0.9px;
  }
}

/* Ribbon no topo centralizado */
.ribbon {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 10;
  min-width: 80px;
  width: auto;
  max-width: calc(100% - 46px);
  height: 18px;
  padding: 0 8px;
  background: rgba(246, 106, 0, 0.6);
  backdrop-filter: blur(8px);
  border-radius: 4px;
  border: 1px solid rgba(246, 106, 0, 0.4);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

@media (min-width: 640px) {
  .ribbon {
    height: 22px;
    min-width: 96px;
    padding: 0 10px;
  }
}

.ribbonText {
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

@media (min-width: 640px) {
  .ribbonText {
    font-size: 11px;
  }
}

.headerSection {
  display: flex;
  align-items: stretch;
  gap: 6px;
  width: 100%;
}

@media (min-width: 640px) {
  .headerSection {
    gap: 10px;
  }
}

.missionImage {
  width: 63px;
  height: 63px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
}

@media (min-width: 640px) {
  .missionImage {
    width: 94px;
    height: 94px;
    border-radius: 8px;
  }
}

.missionInfo {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 3px;
  flex: 1;
  min-width: 0;
  height: 100%;
}

@media (min-width: 640px) {
  .missionInfo {
    gap: 5px;
  }
}

.missionName {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  color: #fff;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
  cursor: pointer;
}

.missionName:hover {
  filter: brightness(0.85);
}

@media (min-width: 640px) {
  .missionName {
    font-size: 19px;
    line-height: 22px;
  }
}

.missionDescription {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  color: #a1a1aa;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
  cursor: pointer;
}

.missionDescription:hover {
  filter: brightness(0.85);
}

@media (min-width: 640px) {
  .missionDescription {
    font-size: 15px;
    line-height: 19px;
  }
}

.rewardSection {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 5px 10px;
  background: rgba(246, 106, 0, 0.1);
  backdrop-filter: blur(6px);
  border-radius: 6px;
  cursor: pointer;
}

.rewardSection:hover {
  filter: brightness(1.1);
}

@media (min-width: 640px) {
  .rewardSection {
    gap: 10px;
    padding: 8px 16px;
    border-radius: 10px;
  }
}

.rewardIcon {
  font-size: 12px;
  color: #f66a00;
}

@media (min-width: 640px) {
  .rewardIcon {
    font-size: 19px;
  }
}

.rewardText {
  font-weight: 700;
  font-size: 10px;
  color: #f66a00;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 640px) {
  .rewardText {
    font-size: 14px;
  }
}

.progressSection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 3px;
}

@media (min-width: 640px) {
  .progressSection {
    gap: 5px;
  }
}

.progressBarContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  padding: 0 6px;
}

@media (min-width: 640px) {
  .progressBarContainer {
    padding: 0 10px;
  }
}

.progressBar {
  flex: 1;
  height: 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 100px;
  position: relative;
  overflow: hidden;
}

@media (min-width: 640px) {
  .progressBar {
    height: 19px;
  }
}

.progressFill {
  height: 100%;
  background: #f66a00;
  border-radius: 100px;
  min-width: fit-content;
}

.progressText {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 800;
  font-size: 7px;
  line-height: 1;
  color: #010612;
}

@media (min-width: 640px) {
  .progressText {
    font-size: 11px;
  }
}

.progressIconLeft {
  position: absolute;
  left: 0;
  font-size: 16px;
  z-index: 1;
}

@media (min-width: 640px) {
  .progressIconLeft {
    font-size: 25px;
  }
}

.progressIconRight {
  position: absolute;
  right: 0;
  font-size: 16px;
  z-index: 1;
}

@media (min-width: 640px) {
  .progressIconRight {
    font-size: 25px;
  }
}

.footerSection {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
}

@media (min-width: 640px) {
  .footerSection {
    gap: 10px;
  }
}

.gamesSection {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 3px 5px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 1000px;
  cursor: pointer;
  flex: 1;
  min-width: 48%;
}

.gamesSection:hover {
  filter: brightness(1.1);
}

@media (min-width: 640px) {
  .gamesSection {
    gap: 5px;
    padding: 5px 8px;
  }
}

.gamesStack {
  display: flex;
  align-items: center;
}

.gameThumb {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  margin-left: -14px;
}

.gameThumb:first-child {
  margin-left: 0;
}

@media (min-width: 640px) {
  .gameThumb {
    width: 27px;
    height: 27px;
    margin-left: -22px;
  }

  .gameThumb:first-child {
    margin-left: 0;
  }
}

.gameImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gamesLabel {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 8px;
  line-height: 1.33;
  color: rgba(255, 255, 255, 0.5);
}

@media (min-width: 640px) {
  .gamesLabel {
    font-size: 12px;
  }
}

.actionButton {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  flex: 1;
  min-width: 0;
  height: 26px;
  padding: 0 10px;
  background: rgba(246, 106, 0, 0.2);
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.actionButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.actionButton:hover:not(:disabled) {
  filter: brightness(1.1);
}

@media (min-width: 640px) {
  .actionButton {
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    border-radius: 8px;
  }
}

.buttonIcon {
  font-size: 10px;
  color: #f66a00;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .buttonIcon {
    font-size: 15px;
  }
}

.buttonText {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 8px;
  line-height: 1.67;
  color: #f66a00;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 640px) {
  .buttonText {
    font-size: 12px;
  }
}
</style>
